import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services/messages';
import { ProjectContextService } from '@data-services/projects';
import { ProjectContext } from '@models/project-context';
import { ProjectUserRole } from '@models/projects';
import { MBMError } from '@errors/mbm-error';

export const customerMessagesAuthGuard: CanActivateFn = async (route) => {
  const dataContext = inject(DataContext);
  const messageService = inject(MessageService);
  const projectContextService = inject(ProjectContextService);
  const router = inject(Router);
  const translate = inject(TranslateService);

  const projectId = route.paramMap.get('projectId') || dataContext.projectContext()?.id;

  if (!projectId) {
    void router.navigate(['/projects']);
    return false;
  }

  const cachedContext = dataContext.projectContext();

  if (cachedContext?.id === projectId && cachedContext.currentUser) {
    if (!hasCustomerMessagesAccess(cachedContext.currentUser.userRole)) {
      rejectAccess(router, messageService, translate, projectId);
      return false;
    }

    void refreshContextInBackground(projectContextService, dataContext, router, messageService, translate, projectId);
    return true;
  }

  try {
    const projectContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);

    if (!projectContext.currentUser) {
      messageService.showErrorMessage(translate.instant('auth.errors.projectAccessDenied'));
      void router.navigate(['/projects']);
      return false;
    }

    dataContext.setProjectContext(projectContext);

    if (!hasCustomerMessagesAccess(projectContext.currentUser.userRole)) {
      rejectAccess(router, messageService, translate, projectId);
      return false;
    }

    return true;
  } catch (error: unknown) {
    if (isAccessRevokedError(error)) {
      revokeProjectAccess(dataContext, router, messageService, translate);
      return false;
    }

    messageService.showErrorMessage(translate.instant('auth.errors.permissionCheckFailed'));
    void router.navigate(['/projects']);
    return false;
  }
};

function hasCustomerMessagesAccess(role: ProjectUserRole): boolean {
  return role === 'Admin' || role === 'ServiceLeader' || role === 'ServiceWorker' || role === 'Customer';
}

async function refreshContextInBackground(projectContextService: ProjectContextService, dataContext: DataContext, router: Router, messageService: MessageService, translate: TranslateService, projectId: string): Promise<void> {
  try {
    const projectContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);

    // The user may have switched projects while the refresh was in flight
    if (dataContext.projectContext()?.id !== projectId) {
      return;
    }

    if (!projectContext.currentUser) {
      revokeProjectAccess(dataContext, router, messageService, translate);
      return;
    }

    dataContext.setProjectContext(projectContext);
  } catch (error: unknown) {
    if (dataContext.projectContext()?.id !== projectId) {
      return;
    }

    if (isAccessRevokedError(error)) {
      revokeProjectAccess(dataContext, router, messageService, translate);
    }
    // Transient failures keep the cached context
  }
}

function isAccessRevokedError(error: unknown): boolean {
  return error instanceof MBMError && (error.statusCode === 401 || error.statusCode === 403 || error.statusCode === 404);
}

function revokeProjectAccess(dataContext: DataContext, router: Router, messageService: MessageService, translate: TranslateService): void {
  dataContext.clearProjectContext();
  messageService.showErrorMessage(translate.instant('auth.errors.projectAccessDenied'));
  void router.navigate(['/projects']);
}

function rejectAccess(router: Router, messageService: MessageService, translate: TranslateService, projectId: string): void {
  messageService.showErrorMessage(translate.instant('auth.errors.customerMessagesAccessDenied'));
  void router.navigate(['/projects', projectId, 'services']);
}
